import React from 'react';
import { Alert, Box, Skeleton, Stack, Typography } from '@mui/material';
import type { Site } from '../../services/sitesApi';
import { WarehouseSelect } from '../ui/WarehouseSelect';
import {
  WORKSPACE_ALL_AUTHORIZED_SITES,
  buildWorkspaceSiteOptions,
} from '../../domain/safetyCases/workspacePortfolioScope';

export function WorkspaceScopeSelector({
  orgId,
  siteId,
  mode = 'site',
  sites,
  loading,
  error,
  onSiteChange,
}: {
  orgId: string;
  siteId: string;
  mode?: 'site' | 'org';
  sites: Site[];
  loading?: boolean;
  error?: string | null;
  onSiteChange: (siteId: string) => void;
}) {
  const hasKnownMultipleSites = sites.length > 1;
  const options = buildWorkspaceSiteOptions(sites, siteId, {
    includeAllAuthorized: Boolean(orgId && (hasKnownMultipleSites || mode === 'org')),
  });
  const selectedValue = mode === 'org' ? WORKSPACE_ALL_AUTHORIZED_SITES : siteId;

  return (
    <Stack spacing={1} data-testid="workspace-scope-selector">
      <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', flexWrap: 'wrap' }}>
        <WarehouseSelect
          label="Site scope"
          value={selectedValue}
          options={options}
          onChange={onSiteChange}
          disabled={loading || options.length === 0}
          minWidth={280}
          data-testid="workspace-site-scope-select"
        />
        <Typography variant="caption" color="text.secondary" data-testid="workspace-org-scope-label">
          {orgId ? `Organization: ${orgId}` : 'Organization scope unavailable'}
        </Typography>
        {loading ? <Skeleton width={96} height={28} /> : null}
      </Box>

      {mode === 'org' ? (
        <Alert severity="info" data-testid="workspace-scope-portfolio">
          Org portfolio mode shows authorized sites together with coverage context. Counts are operational workload counts, not a risk score, danger score, ranking, or safety-performance conclusion.
        </Alert>
      ) : error ? (
        <Alert severity="warning" data-testid="workspace-scope-warning">
          Site list unavailable. The workspace remains scoped to {siteId || 'the current site'}; portfolio-wide switching may be incomplete.
        </Alert>
      ) : hasKnownMultipleSites ? (
        <Alert severity="info" data-testid="workspace-scope-partial">
          Multi-site mode can switch between authorized sites or show the org portfolio with coverage context. No aggregate risk score is shown.
        </Alert>
      ) : null}
    </Stack>
  );
}
